/**
 * What the business owns and owes, on one day.
 *
 * HOW IT DIFFERS FROM THE PROFIT AND LOSS
 * ---------------------------------------
 * The profit and loss covers a stretch of time and is emptied each year. This
 * is a photograph of a single moment, counting everything from the first day
 * of trading up to it — so it takes one date, not two.
 *
 * WHY THE BALANCE CHECK IS ON THE PAGE
 * ------------------------------------
 * The two halves must agree to the paisa, and normally do, because every
 * entry in the books balances on its own. That is exactly why it is worth
 * showing: if they ever stop agreeing, something is wrong that no other
 * screen would reveal, and the person reading this is the one who needs to
 * know.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ReportExport } from './ReportExport.jsx';
import { ReportTabs } from './ReportTabs.jsx';

export default function BalanceSheetPage() {
  const [asAt, setAsAt] = useState(today);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'balance-sheet', asAt],
    queryFn: () => api.get(withQuery('/reports/balance-sheet', { as_at: asAt })),
  });

  return (
    <div className="mx-auto max-w-3xl p-4">
      <ReportTabs />
      <div className="no-print mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Balance sheet</h1>
          <p className="mt-0.5 text-sm text-ink-2">What the business owns and owes, on one day.</p>
          <div className="mt-2.5">
            <ReportExport
              path={withQuery('/reports/balance-sheet', { as_at: asAt })}
              name={`Balance sheet ${asAt}`}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label>
            <span className="mb-1.5 block text-xs text-ink-3">As at</span>
            <input
              type="date"
              value={asAt}
              onChange={(e) => setAsAt(e.target.value)}
              className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          </label>
          <Button variant="secondary" onClick={() => setAsAt(today())}>
            Today
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Working it out" />
        </div>
      ) : isError || !data ? (
        <p className="py-16 text-center text-sm text-ink-2">
          Could not work this out. Check the branch computer is running.
        </p>
      ) : (
        <>
          {data.coverage?.warning && (
            <p className="no-print mb-4 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
              {data.coverage.warning}
            </p>
          )}

          {/* Loud, because a balance sheet that does not balance means the
              books themselves are damaged — not merely this screen. */}
          {!data.balanced && (
            <p className="mb-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
              This does not balance. What the business owns is{' '}
              {formatPKR(Math.abs(data.difference))} {data.difference > 0 ? 'more' : 'less'} than
              what it owes plus what the owners have put in, which should never happen. The books
              need looking at before anyone relies on these figures.
            </p>
          )}

          <div className="statement rounded-xl border border-rule bg-surface p-5">
            <header className="mb-4 border-b border-rule pb-3 text-center">
              <h2 className="font-semibold text-ink">Balance sheet</h2>
              <p className="text-sm text-ink-2">as at {data.asAt}</p>
            </header>

            <Section title="What it owns now" lines={data.assets.current.lines} />
            <Section title="Equipment and property" lines={data.assets.fixed.lines} />
            <Section title="Other assets" lines={data.assets.other.lines} />

            <Total label="Total assets" value={data.assets.total} emphasis />

            <div className="mt-6">
              <Section title="What it owes" lines={data.liabilities.lines} />
              <Total label="Total liabilities" value={data.liabilities.total} />
            </div>

            <div className="mt-6">
              <Section
                title="Owners' stake"
                lines={[
                  ...data.equity.lines,
                  /* Shown on its own line rather than folded into retained
                     earnings, because those are different facts: one has been
                     booked into the accounts and one has not yet. */
                  {
                    code: '',
                    name: data.equity.earnings.label,
                    amount: data.equity.earnings.amount,
                  },
                ]}
              />
              <Total label="Total owners' stake" value={data.equity.total} />
            </div>

            <div
              className={`mt-4 flex items-baseline justify-between border-t-2 pt-3 ${
                data.balanced ? 'border-brand-400' : 'border-bad'
              }`}
            >
              <span className="font-semibold text-ink">Liabilities and owners&rsquo; stake</span>
              <span className="text-right">
                <span className="block text-2xl font-semibold tabular-nums text-ink">
                  {formatPKR(data.liabilities.total + data.equity.total)}
                </span>
                <span
                  className={`block text-xs ${data.balanced ? 'text-ink-3' : 'font-medium text-bad'}`}
                >
                  {data.balanced
                    ? 'Matches total assets, as it must'
                    : `Out by ${formatPKR(Math.abs(data.difference))}`}
                </span>
              </span>
            </div>
          </div>

          <div className="no-print mt-4 flex justify-end">
            <Button variant="secondary" onClick={() => window.print()}>
              Print
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, lines }) {
  // A heading with nothing under it says less than no heading at all.
  if (!lines.length) return null;

  return (
    <section className="mt-4 first:mt-0">
      <h3 className="text-sm font-medium text-ink-2">{title}</h3>

      <dl className="mt-1.5">
        {lines.map((line) => (
          <div
            key={line.code || line.name}
            className="flex items-baseline justify-between gap-3 py-0.5"
          >
            <dt className="min-w-0 text-sm text-ink-2">
              {/* The account code, so a figure can be traced into the ledger
                  rather than taken on trust. */}
              {line.code && (
                <span className="mr-2 text-xs tabular-nums text-ink-3">{line.code}</span>
              )}
              {line.name}
            </dt>
            {/* A negative figure is shown in brackets, which is how the trade
                writes it and how anyone reading a statement expects to see it.
                Depreciation and an overdrawn account both land here. */}
            <dd
              className={`shrink-0 text-sm tabular-nums ${
                line.amount < 0 ? 'text-ink-2' : 'text-ink'
              }`}
            >
              {line.amount < 0 ? `(${formatPKR(Math.abs(line.amount))})` : formatPKR(line.amount)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Total({ label, value, emphasis = false }) {
  return (
    <div
      className={`mt-1.5 flex items-baseline justify-between border-t border-rule pt-1.5 ${
        emphasis ? 'mt-3' : ''
      }`}
    >
      <span className={`text-sm ${emphasis ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>
        {label}
      </span>
      <span
        className={`tabular-nums ${emphasis ? 'text-lg font-semibold text-ink' : 'font-medium text-ink'}`}
      >
        {formatPKR(value)}
      </span>
    </div>
  );
}
